import { useCallback, useEffect, useRef, useState } from "react";
import type { TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/site";

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = heroSlides.length;

  const goTo = useCallback(
    (index: number) => setCurrent((index + count) % count),
    [count],
  );

  useEffect(() => {
    const timer = setInterval(() => setCurrent((v) => (v + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(current + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-foreground-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点剧情轮播"
    >
      {heroSlides.map((slide, idx) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === current ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 宣传画面`}
            title={`${slide.title} - ${slide.subtitle}`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/60"></div>
        </div>
      ))}

      <div className="relative z-20 w-full h-full flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-background-50/30 text-background-50/90 font-label text-xs tracking-[0.2em] uppercase">
              影视内容门户
            </span>
            <h1 className="mt-4 font-heading text-4xl md:text-6xl font-bold text-background-50 leading-tight">
              朋友的妈妈3
            </h1>
            <p className="mt-2 text-sm md:text-lg text-background-100/80">
              最新剧情介绍 · 影视资讯 · 剧集资料
            </p>
            <div className="mt-6 md:mt-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/90 text-primary-950 text-xs font-medium whitespace-nowrap">
                <i className="ri-fire-line"></i>
                {heroSlides[current].tag}
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
                {heroSlides[current].title}
              </h2>
              <p className="mt-1.5 font-label text-base md:text-lg text-accent-400 tracking-wide">
                {heroSlides[current].subtitle}
              </p>
              <p className="mt-3 text-sm md:text-base text-background-100/80 leading-relaxed max-w-2xl line-clamp-2">
                {heroSlides[current].desc}
              </p>
            </div>
            <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={heroSlides[current].link}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill"></i>
                {heroSlides[current].cta}
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览全部剧情
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(current - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 hover:bg-accent-500 hover:text-primary-950 text-background-50 backdrop-blur transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(current + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 hover:bg-accent-500 hover:text-primary-950 text-background-50 backdrop-blur transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
        {heroSlides.map((slide, idx) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(idx)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              idx === current ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
            aria-label={`切换到第 ${idx + 1} 张`}
          ></button>
        ))}
      </div>
    </section>
  );
}